import { useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Shirt } from "../../entities/Shirt";
import DialogService from "../Dialog/dialogService";
import { AbilityContext } from "../../permission/Can";
import { Actions, Subjects } from "../../permission/constants";
import JoinPremiumPopUp from "../JoinPremiumPopUp/JoinPremiumPopUp";

interface ShirtSelectorProps {
  options: Array<Shirt>;
  setSelection: Function;
}

const ShirtSelector = ({ options, setSelection }: ShirtSelectorProps) => {
  const [selected, setSelected] = useState<Shirt | null>(null);

  const saveSelection = () => {
    if (selected) {
      setSelection(selected);
    }
  };

  return (
    <Box sx={{ marginY: "1rem" }}>
      <Autocomplete
        fullWidth
        sx={{
          marginBottom: "1rem",
          backgroundColor: "#f4f6f9 !important",
          ".MuiInputBase-input": {
            display: "flex",
            alignItems: "center",
            backgroundColor: "#f4f6f9 !important",
            borderRadius: "5px !important",
            fontWeight: 700,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: "0px",
          },
          "& .MuiOutlinedInput-input": {
            fontFamily: "Manrope",
            color: "#6f7d95",
            fontSize: "1rem !important",
            fontWeight: 700,
          },
        }}
        getOptionLabel={(option: Shirt) => (option.title ? option.title : "")}
        options={options}
        renderInput={(params) => <TextField {...params} label="Shirt" />}
        value={selected}
        onChange={(event: any, newValue: Shirt | null) => {
          setSelected(newValue);
        }}
      />

      <Box sx={{ width: "fit-content", marginLeft: "auto" }}>
        <Button
          onClick={saveSelection}
          disabled={selected === null}
          variant="customBlueText"
        >
          Select
        </Button>
      </Box>
    </Box>
  );
};

interface Top10ElementAddProps {
  position: number;
  add?: Function;
  shirtCollection: Array<Shirt>;
}

const Top10ElementAdd = ({
  position,
  add,
  shirtCollection,
}: Top10ElementAddProps) => {
  const [shirt, setShirt] = useState<Shirt | undefined>(undefined);

  const { openXS: open, close } = DialogService();

  const ability = useContext(AbilityContext);

  const selectShirt = () => {
    if (ability.can(Actions.UPDATE, Subjects.TOP10)) {
      open(
        "Select a shirt",
        <ShirtSelector options={shirtCollection} setSelection={setShirt} />,
        []
      );
    } else {
      open("", <JoinPremiumPopUp />, []);
    }
  };

  const addItem = () => {
    if (add && shirt) {
      add(shirt, position);
      close();
    }
  };

  useEffect(() => {
    if (shirt) {
      addItem();
    }
  }, [shirt]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        padding: "1rem",
        backgroundColor: "#fff !important",
        borderRadius: "10px",
      }}
    >
      <Box sx={{ marginRight: "1rem" }}>
        <Avatar
          component={Paper}
          elevation={2}
          sx={{ padding: "5px", backgroundColor: "white" }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#3c3c40",
              fontWeight: "700",
              fontSize: "1.75rem !important",
              fontFamily: "Manrope",
            }}
          >
            {position}
          </Typography>
        </Avatar>
      </Box>
      <Box>
        {add ? (
          <Button
            startIcon={
              <AddCircleOutlineIcon sx={{ fontSize: "30px !important" }} />
            }
            variant="topAddButton"
            onClick={selectShirt}
          >
            Add new shirt
          </Button>
        ) : (
          ""
        )}
      </Box>
    </Box>
  );
};

export default Top10ElementAdd;
